import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  CanActivate,
} from '@angular/router';

import { UAMAuthService } from '../services/uam-auth.service';
import { environment } from 'src/environments/environment';

const HOME = '/admin';

const ROUTE_ACCESS: { prefix: string; roles: readonly string[] }[] = [
  {
    prefix: '/admin/assignments',
    roles: ['standalone', 'base', 'base-seo', 'organization-base', 'pavcon'],
  },
  {
    prefix: '/admin/search-artifacts',
    roles: ['pavcon-user', 'pavcon'],
  },
  {
    prefix: '/admin/forms-data-lookup',
    roles: ['pavcon-user', 'pavcon'],
  },
  {
    prefix: '/admin/sessions',
    roles: [
      'organization-user',
      'organization-admin',
      'organization-base',
      'pavcon',
    ],
  },
  {
    prefix: '/admin/results',
    roles: [
      'organization-user',
      'organization-admin',
      'organization-base',
      'pavcon',
    ],
  },
  {
    prefix: '/admin/forms-insights-hub',
    roles: [
      'organization-user',
      'organization-admin',
      'organization-base',
      'pavcon',
    ],
  },
  {
    prefix: '/admin/surveys',
    roles: ['organization-admin', 'organization-base', 'pavcon'],
  },
  {
    prefix: '/admin/survey',
    roles: ['organization-admin', 'organization-base', 'pavcon'],
  },
  { prefix: '/admin/form-builder', roles: ['pavcon'] },
  { prefix: '/admin/project-manager', roles: ['pavcon'] },
  { prefix: '/admin/user-manager', roles: ['pavcon'] },
  { prefix: '/admin/roles-permissions', roles: ['pavcon'] },
  { prefix: '/admin/review-access-request', roles: ['pavcon'] },
];

const KNOWN_ROLES = new Set(ROUTE_ACCESS.flatMap((entry) => entry.roles));

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  constructor(
    private uamService: UAMAuthService,
    private router: Router
  ) {}

  canActivate(
    _next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const userDetails: any = this.uamService.getAuthenticatedUser();
    const role = userDetails?.['custom:warfighter_role']?.trim() || '';
    if (!userDetails || !role || !KNOWN_ROLES.has(role)) {
      console.warn(
        'Access denied - You do not have permission to access this page'
      );
      window.location.href = environment.uamUrl;
      return false;
    }

    if (this.isRouteAllowed(role, state.url)) {
      return true;
    }

    this.router.navigate(['/admin']);
    return false;
  }

  /** Ignores the query string and a trailing slash. */
  private isRouteAllowed(role: string, url: string): boolean {
    const path = url.split('?')[0].replace(/\/+$/, '') || '/';
    if (path === HOME) {
      return true;
    }

    return ROUTE_ACCESS.some((entry) => {
      const matchesPath =
        path === entry.prefix || path.startsWith(`${entry.prefix}/`);
      return matchesPath && entry.roles.includes(role);
    });
  }
}
